import HtmlParser from 'html-react-parser';
import React, { memo } from 'react';

import TMIcon from 'tm-icons-library';
import { useTheme } from '../../../../context/ThemeProvider';
import { isHtml } from '../../../../shared/utils';
import {
  AssignmentQuestionCategory,
  IAssignmentQuestionOption,
  IObtainedMarksForQuestion,
} from '../../../../types/assignmentTypes';
import { useAssignmentContext } from '../../utils/AssignmentProvider';

interface Props {
  questionId: string;
  question?: IObtainedMarksForQuestion;
  selectedOptions: number[];
}

const AssignmentQuestionOptions: React.FC<Props> = ({
  questionId,
  question,
  selectedOptions,
}) => {
  const { colors } = useTheme();

  const {
    type,
    options = [],
    correctOptions = [],
    selectedOptions: actualSelectedOptions = [],
  } = question || {};

  const { saveQuestionAnswer, flow } = useAssignmentContext();

  const isMultipleSelect = type === AssignmentQuestionCategory.MULTIPLE_SELECT;

  const handleOptionSelect = (optionIndex: number) => {
    let newSelectedOptions: number[];

    if (isMultipleSelect) {
      // For multiple select, toggle the option
      if (selectedOptions.includes(optionIndex + 1)) {
        newSelectedOptions = selectedOptions.filter(
          (option) => option !== optionIndex + 1,
        );
      } else {
        newSelectedOptions = [...selectedOptions, optionIndex + 1];
      }
    } else {
      // For single select, replace the selection
      newSelectedOptions = [optionIndex + 1];
    }

    saveQuestionAnswer(questionId, 'save', {
      selectedOptions: newSelectedOptions,
    });
  };

  const renderOptionItemBox = (
    q: IObtainedMarksForQuestion | undefined,
    option: IAssignmentQuestionOption,
    optionIndex: number,
  ) => {
    if (!q) return null;
    return (
      <div style={{ flex: 1, position: 'relative' }}>
        {option.type === 'text' && option.text ? (
          <div style={{ marginBottom: isHtml(option.text) ? -12 : 0 }}>
            {HtmlParser(option.text || '')}
          </div>
        ) : (
          <img
            src={option.image}
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 6,
              objectFit: 'cover',
            }}
            alt="Option image"
          />
        )}

        {flow === 'review' ? (
          correctOptions.includes(optionIndex + 1) ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: option.image ? 6 : 0,
                right: option.image ? 6 : 0,
              }}>
              <TMIcon name="checkmark" size={18} color={colors.GREEN} />
            </div>
          ) : actualSelectedOptions.includes(optionIndex + 1) ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: option.image ? 6 : 0,
                right: option.image ? 6 : 0,
              }}>
              <TMIcon name="close" size={18} color={colors.DANGER} />
            </div>
          ) : null
        ) : null}
      </div>
    );
  };

  const renderSelectionIndicator = (optionIndex: number) => {
    const isSelected = selectedOptions.includes(optionIndex + 1);

    const baseIndicatorStyle = {
      width: 20,
      height: 20,
      border: '2px solid #007bff',
      alignSelf: 'flex-start' as const,
      marginTop: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const selectedStyle = isSelected ? { backgroundColor: '#007bff' } : {};
    const checkboxStyle = isMultipleSelect ? { borderRadius: 4 } : {};
    const radioStyle = !isMultipleSelect ? { borderRadius: 10 } : {};

    return (
      <div
        style={{
          ...baseIndicatorStyle,
          ...selectedStyle,
          ...checkboxStyle,
          ...radioStyle,
        }}>
        {isMultipleSelect && isSelected && (
          <div
            style={{
              width: 10,
              height: 6,
              borderLeft: '2px solid white',
              borderBottom: '2px solid white',
              transform: 'rotate(-45deg)',
              marginTop: -1,
            }}
          />
        )}
        {!isMultipleSelect && isSelected && (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: 'white',
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 24 }}>
      {options.map((option, index) => (
        <div
          key={`question-${question?.order}-answer-option-${index}`}
          style={{
            marginBottom: 12,
            cursor: 'pointer',
            opacity: 1,
            transition: 'opacity 0.2s ease',
          }}
          onClick={() => {
            if (flow === 'submit') {
              handleOptionSelect(index);
            }
          }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {flow === 'submit' ? renderSelectionIndicator(index) : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: 12,
                backgroundColor:
                  flow === 'submit'
                    ? selectedOptions.includes(index + 1)
                      ? '#e3f2fd'
                      : '#f8f9fa'
                    : flow === 'review'
                    ? correctOptions.includes(index + 1)
                      ? `${colors.GREEN}40`
                      : actualSelectedOptions.includes(index + 1)
                      ? `${colors.DANGER}40`
                      : '#f8f9fa'
                    : '#f8f9fa',
                borderRadius: 8,
                border: `1px solid ${
                  flow === 'submit'
                    ? selectedOptions.includes(index + 1)
                      ? '#2196f3'
                      : '#e9ecef'
                    : flow === 'review'
                    ? correctOptions.includes(index + 1)
                      ? `${colors.GREEN}40`
                      : actualSelectedOptions.includes(index + 1)
                      ? `${colors.DANGER}40`
                      : '#e9ecef'
                    : '#e9ecef'
                }`,
                flex: 1,
              }}>
              {renderOptionItemBox(question, option, index)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo(AssignmentQuestionOptions);
